import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useNavigate, useOutletContext, useParams } from "react-router-dom";

import { Preloader } from "@/components";
import { AdminChatPayload } from "@/features/chats";
import { useTrainerChats } from "../model/TrainerChatsContext";

import styles from "./TrainerChatUserLayout.module.scss";

type TrainerChatOutletContext = {
  userId: number;
  profile: AdminChatPayload;
};

export const useTrainerChatOutlet = () =>
  useOutletContext<TrainerChatOutletContext>();

export const TrainerChatUserLayout = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const {
    ensureChats,
    chats,
    chatsLoading,
    getCachedChat,
    getChat,
    isChatsLoaded,
  } = useTrainerChats();

  const [isProfileLoading, setProfileLoading] = useState(false);
  const profileRequestedRef = useRef<number | null>(null);

  const numericUserId = useMemo(() => {
    if (!userId) {
      return null;
    }

    const parsed = Number(userId);
    return Number.isNaN(parsed) ? null : parsed;
  }, [userId]);

  useEffect(() => {
    ensureChats();
  }, [ensureChats]);

  useEffect(() => {
    if (numericUserId === null) {
      navigate("/trainer/clients", { replace: true });
    }
  }, [navigate, numericUserId]);

  useEffect(() => {
    if (
      numericUserId !== null &&
      isChatsLoaded &&
      !chatsLoading &&
      chats.length > 0
    ) {
      const exists = chats.some((chat) => chat.user_id === numericUserId);
      if (!exists) {
        navigate("/trainer/clients", { replace: true });
      }
    }
  }, [chats, chatsLoading, isChatsLoaded, navigate, numericUserId]);

  const profile = useMemo(() => {
    if (numericUserId === null) {
      return null;
    }

    return getCachedChat(numericUserId) ?? null;
  }, [getCachedChat, numericUserId]);

  useEffect(() => {
    const loadProfile = async () => {
      if (numericUserId === null || profile || isProfileLoading) {
        return;
      }

      if (profileRequestedRef.current === numericUserId) {
        return;
      }

      profileRequestedRef.current = numericUserId;

      setProfileLoading(true);
      const result = await getChat(numericUserId);
      setProfileLoading(false);

      if (result === null) {
        profileRequestedRef.current = null;
        navigate("/trainer/clients", { replace: true });
      }
    };

    loadProfile();
  }, [getChat, isProfileLoading, navigate, numericUserId, profile]);

  useEffect(() => {
    if (profile && profileRequestedRef.current === profile.user_id) {
      profileRequestedRef.current = null;
    }
  }, [profile]);

  if (numericUserId === null) {
    return null;
  }

  if ((isProfileLoading || chatsLoading) && !profile) {
    return (
      <div className={styles.loader}>
        <Preloader />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={styles.placeholder}>
        Клиент не найден или данные недоступны.
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Outlet context={{ userId: numericUserId, profile }} />
    </div>
  );
};

